import { SubscriptionContext } from "@database/contexts";
import { subscriptionService } from "@database/services";
import { ErrorType } from "@utilities/types";
import { useCallback, useContext, useState } from "react"
import { useOrganisation } from "./useOrganisation";

export const useSubscriptions = ()=> {

    const {
        plans, setPlans,
        modules, setModules,
        organisationModules, setOrganisationModules,
        nextInvoice, setNextInvoice,
        InvoiceHistory, setInvoiceHistory,
    } = useContext(SubscriptionContext);
    const { organisation } = useOrganisation();

    const [getAllPlansError, setGetAllPlansError] = useState<ErrorType | null>(null);
    const [getAllPlansLoading, setGetAllPlansLoading] = useState<boolean>(false);
    const [getOrganisationSubscriptionModulesError, setGetOrganisationSubscriptionModulesError] = useState<ErrorType | null>(null);
    const [getOrganisationSubscriptionModulesLoading, setGetOrganisationSubscriptionModulesLoading] = useState<boolean>(false);
    const [initSubscriptionModuleError, setInitSubscriptionModuleError] = useState<ErrorType | null>(null);
    const [initSubscriptionModuleLoading, setInitSubscriptionModuleLoading] = useState<boolean>(false);
    const [getNextInvoiceDetailsError, setGetNextInvoiceDetailsError] = useState<ErrorType | null>(null);
    const [getNextInvoiceDetailsLoading, setGetNextInvoiceDetailsLoading] = useState<boolean>(false);
    const [getOrganisationInvoiceHistoryError, setGetOrganisationInvoiceHistoryError] = useState<ErrorType | null>(null);
    const [getOrganisationInvoiceHistoryLoading, setGetOrganisationInvoiceHistoryLoading] = useState<boolean>(false);
    const [updateOrganisationModulesError, setUpdateOrganisationModulesError] = useState<ErrorType | null>(null);
    const [updateOrganisationModulesLoading, setUpdateOrganisationModulesLoading] = useState<boolean>(false);

    const onGetAllPlans = useCallback(async()=> {
        setGetAllPlansLoading(true);
        const req = await subscriptionService.getAllPlans();
        
        if(req.code === 200){
            setGetAllPlansError(null);
            setPlans(req.data.plans);
            setModules(req.data.modules);
        } else {
            setGetAllPlansError({
                message: "Erreur lors de la récupération des plans",
            });
        }
        setGetAllPlansLoading(false);
    }, [setModules, setPlans]);

    const onGetOrganisationSubscriptionModules = useCallback(async()=> {
        if(!organisation) return;

        setGetOrganisationSubscriptionModulesLoading(true);
        const req = await subscriptionService.getOrganisationSubscriptionModules(organisation._id);
        
        if(req.code === 200){
            setGetOrganisationSubscriptionModulesError(null);
            setOrganisationModules(req.data.data);
        } else {
            setGetOrganisationSubscriptionModulesError({
                message: req.code === 400 ? 
                            "Données invalides"
                            : "Erreur lors de la récupération des modules d'abonnement",
            });
        }
        setGetOrganisationSubscriptionModulesLoading(false);
    }, [organisation, setOrganisationModules]);

    const onInitSubscriptionModule = useCallback(async()=> {
        if(!organisation) return;

        setInitSubscriptionModuleLoading(true);
        const req = await subscriptionService.initSubscriptionModule(organisation._id);
        if(req.code === 300 || req.code === 200){
            setInitSubscriptionModuleError(null);
            await onGetOrganisationSubscriptionModules();
        } else {
            setInitSubscriptionModuleError({
                message: req.code === 400 ? 
                            "Données invalides"
                            : "Erreur lors de l'initialisation du module d'abonnement",
            });
        }
        setInitSubscriptionModuleLoading(false);
    }, [onGetOrganisationSubscriptionModules, organisation]);

    const onGetNextInvoiceDetails = useCallback(async()=> {
        if(!organisation) return;

        setGetNextInvoiceDetailsLoading(true);
        const req = await subscriptionService.getNextInvoiceDetails(organisation._id);
        if(req.code === 200){
            setGetNextInvoiceDetailsError(null);
            setNextInvoice(req.data);
        } else {
            setGetNextInvoiceDetailsError({
                message: req.code === 404 ?
                            "Aucune facture à venir"
                            : "Erreur lors de la récupération des détails de la prochaine facture",
            });
        }
        setGetNextInvoiceDetailsLoading(false);
    }, [organisation, setNextInvoice]);

    const onGetOrganisationInvoiceHistory = useCallback(async()=> {
        if(!organisation) return;

        setGetOrganisationInvoiceHistoryLoading(true);
        const req = await subscriptionService.getOrganisationInvoiceHistory(organisation._id);
        if(req.code === 200){
            setGetOrganisationInvoiceHistoryError(null);
            setInvoiceHistory(req.data.data);
        } else {
            setGetOrganisationInvoiceHistoryError({
                message: req.code === 404 ?
                            "Aucune facture trouvée"
                            : "Erreur lors de la récupération de l'historique des factures",
            });
        }
        setGetOrganisationInvoiceHistoryLoading(false);
    }, [organisation, setInvoiceHistory]);

    const onUpdateOrganisationModules = useCallback(async(name: string, option: string, price: number)=> {
        if(!organisation) return;
        
        setUpdateOrganisationModulesLoading(true);
        const req = await subscriptionService.updateOrganisationModules(organisation._id, name, option, price);
        if(req.code === 200){
            setUpdateOrganisationModulesError(null);
            await onGetOrganisationSubscriptionModules();
        } else {
            setUpdateOrganisationModulesError({
                message: (req.code === 400 || req.code === 404) ? 
                            "Données invalides, module non trouvé"
                            : "Erreur lors de la mise à jour des modules d'abonnement",
            });
        }
        setUpdateOrganisationModulesLoading(false);
    }, [organisation, onGetOrganisationSubscriptionModules]);

    return {
        plans, modules, getAllPlansLoading,
        getAllPlansError, organisationModules,
        getOrganisationSubscriptionModulesError, InvoiceHistory,
        getOrganisationSubscriptionModulesLoading, nextInvoice,
        initSubscriptionModuleError, initSubscriptionModuleLoading,
        getNextInvoiceDetailsError, getNextInvoiceDetailsLoading,
        getOrganisationInvoiceHistoryError, getOrganisationInvoiceHistoryLoading,
        updateOrganisationModulesError, updateOrganisationModulesLoading,
        onGetAllPlans, onGetOrganisationSubscriptionModules,
        onInitSubscriptionModule, onGetNextInvoiceDetails,
        onGetOrganisationInvoiceHistory, onUpdateOrganisationModules,
    }
}
